import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Receipt, Download, ArrowLeft, IndianRupee, Package, Calendar, User, Phone, MapPin, Stethoscope, AlertCircle } from 'lucide-react';
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ViewInvoice = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const invoiceRes = await axiosInstance.get(`/invoice/${invoiceId}`);
                setInvoice(invoiceRes.data);
            } catch (err) {
                console.error('Error:', err);
                setError('Failed to load invoice');
            } finally {
                setLoading(false);
            }
        };
        if (invoiceId) fetchData();
    }, [invoiceId]);

    const generatePDF = () => {
        if (!invoice) return;

        // A5 size in portrait mode (148mm x 210mm)
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a5'
        });

        const pageWidth = doc.internal.pageSize.width;
        const margin = 8;
        let yPos = 10;

        // Header - LIFELINE MEDICAL
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text('LIFELINE MEDICAL', pageWidth / 2, yPos, { align: 'center' });
        yPos += 5;

        // Address
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text('2ND FLOOR, SHOP NO.221, NOOR PLAZA, NR. MOMINSHAH BAVA DARGAH', pageWidth / 2, yPos, { align: 'center' });
        yPos += 3.5;
        doc.text('OPP. MARKKETING YARD, NH-27, CHANDRAPUR - 363621 | WANKANER(M), MORBI', pageWidth / 2, yPos, { align: 'center' });
        yPos += 3.5;
        doc.text('Lic. GJ-MOR-256412 - 256413 | Ph: +91 63564 86926', pageWidth / 2, yPos, { align: 'center' });
        yPos += 6;

        // Horizontal Line
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;

        // Invoice Info Row
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(`Invoice No: ${invoiceId}`, margin, yPos);
        doc.text(`Date: ${new Date(invoice.invoice_date).toLocaleDateString('en-IN')}`, pageWidth - margin, yPos, { align: 'right' });
        yPos += 5;

        // Customer Details
        doc.setFont('helvetica', 'normal');
        doc.text(`Name: ${invoice.name || 'N/A'}`, margin, yPos);
        yPos += 4;
        
        // Mobile and Village on same line
        doc.text(`Mobile: ${invoice.mobile_number || 'N/A'}`, margin, yPos);
        if (invoice.village) {
            doc.text(`Village: ${invoice.village}`, pageWidth - margin, yPos, { align: 'right' });
        }
        yPos += 4;
        
        if (invoice.doctor) {
            doc.text(`Doctor: ${invoice.doctor}`, margin, yPos);
            yPos += 4;
        }
        yPos += 2;

        // Items Table
        // Check if any item has returned quantity > 0
        const hasReturns = invoice.InvoiceItems.some(item => (item.returned_quantity || 0) > 0);

        // Items Table
        const items = invoice.InvoiceItems.map((item, index) => {
            const row = [
                (index + 1).toString(),
                item.medicines?.name || 'Unknown',
                item.batch_number || '-',
                item.quantity.toString()
            ];
            
            if (hasReturns) {
                row.push((item.returned_quantity || 0).toString());
            }
            
            row.push(
                parseFloat(item.price_per_unit || 0).toFixed(2),
                parseFloat(item.total_price || 0).toFixed(2)
            );
            
            return row;
        });

        const tableHeaders = ['#', 'Medicine Name', 'Batch', 'Qty'];
        if (hasReturns) {
            tableHeaders.push('Returned');
        }
        tableHeaders.push('Rate', 'Amount');

        const columnStyles = {
            0: { cellWidth: 8, halign: 'center' },
            1: { cellWidth: hasReturns ? 52 : 60 },
            2: { cellWidth: hasReturns ? 20 : 24 },
            3: { cellWidth: 10, halign: 'center' }
        };

        let colIndex = 4;
        if (hasReturns) {
            columnStyles[colIndex] = { cellWidth: 12, halign: 'center' };
            colIndex++;
        }
        columnStyles[colIndex] = { cellWidth: 15, halign: 'right' };
        columnStyles[colIndex + 1] = { cellWidth: 'auto', halign: 'right', fontStyle: 'bold' };

        autoTable(doc, {
            startY: yPos,
            head: [tableHeaders],
            body: items,
            theme: 'plain',
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                fontSize: 8,
                lineWidth: 0.3,
                lineColor: [0, 0, 0],
                halign: 'left'
            },
            bodyStyles: {
                fontSize: 7,
                textColor: [0, 0, 0]
            },
            styles: {
                cellPadding: 1.5,
                lineWidth: 0.3,
                lineColor: [0, 0, 0]
            },
            columnStyles: columnStyles,
            margin: { left: margin, right: margin }
        });

        const finalY = doc.lastAutoTable.finalY + 3;

        // Summary Section
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(margin, finalY, pageWidth - margin, finalY);

        let summaryY = finalY + 5;
        const labelX = pageWidth - margin - 45;
        const valueX = pageWidth - margin;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);

        // Payment Mode
        const paymentMode = invoice.payment_mode?.toUpperCase().replace('_', ' ') || 'N/A';
        doc.text(`Payment Mode: ${paymentMode}`, margin, summaryY);

        // Sub Total
        const subTotal = invoice.InvoiceItems.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0);
        doc.text('Sub Total:', labelX, summaryY);
        doc.text(subTotal.toFixed(2), valueX, summaryY, { align: 'right' });
        summaryY += 4;

        // Round Off
        const roundAmount = parseFloat(invoice.round_amount || 0);
        doc.text('Round Off:', labelX, summaryY);
        doc.text(roundAmount >= 0 ? `+${roundAmount.toFixed(2)}` : roundAmount.toFixed(2), valueX, summaryY, { align: 'right' });
        summaryY += 4;

        // Returned amount - price_per_unit already includes GST
        const pdfReturned = invoice.InvoiceItems.reduce((sum, item) => {
            const returnedQty = parseFloat(item.returned_quantity || 0);
            if (!returnedQty) return sum;
            const pricePerUnit = parseFloat(item.price_per_unit || 0); // Already includes GST
            const discPerUnit = (item.discount_amount && item.quantity) 
                ? (parseFloat(item.discount_amount) / parseFloat(item.quantity)) 
                : 0;
            return sum + returnedQty * (pricePerUnit - discPerUnit);
        }, 0);

        if (pdfReturned > 0) {
            doc.text('Returned:', labelX, summaryY);
            doc.text(`-${pdfReturned.toFixed(2)}`, valueX, summaryY, { align: 'right' });
            summaryY += 4;
        }

        // ✅ Total Amount = subTotal (already has GST) + round off - returned
        const totalAmount = subTotal + roundAmount - pdfReturned;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('Total Amount:', labelX, summaryY);
        doc.text(totalAmount.toFixed(2), valueX, summaryY, { align: 'right' });
        summaryY += 6;

        // if (invoice.payment_mode === 'cash_and_online') {
        //     summaryY += 4;
        //     const cashAmt = parseFloat(invoice.cash_amount || 0).toFixed(2);
        //     const onlineAmt = parseFloat(invoice.online_amount || 0).toFixed(2);
        //     doc.text(`Cash: ${cashAmt} | Online: ${onlineAmt}`, margin, summaryY);
        // } else if (invoice.payment_mode === 'debits') {
        //     summaryY += 4;
        //     const debitsAmt = parseFloat(invoice.debits_amount || 0).toFixed(2);
        //     doc.text(`Debits Amount: ${debitsAmt}`, margin, summaryY);
        // }

        doc.save(`Invoice_${invoiceId}.pdf`);
    };

    if (loading) {
        return (
            <Authenticated>
                <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-600 border-t-transparent mx-auto mb-4"></div>
                        <p className="text-teal-700 font-semibold">Loading Invoice...</p>
                    </div>
                </div>
            </Authenticated>
        );
    }

    if (error || !invoice) {
        return (
            <Authenticated>
                <div className="min-h-screenflex items-center justify-center p-6">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="h-12 w-12 text-red-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Invoice Not Found</h3>
                        <p className="text-gray-600 mb-6">Invoice ID: #{invoiceId}</p>
                        <button
                            onClick={() => navigate('/invoice')}
                            className="px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all font-semibold"
                        >
                            Back to Invoices
                        </button>
                    </div>
                </div>
            </Authenticated>
        );
    }

    const formatDate = (date) => new Date(date).toLocaleDateString('en-IN');
    
    // total_price already includes GST and discount
    const subtotal = invoice.InvoiceItems.reduce((s, i) => s + parseFloat(i.total_price || 0), 0);
    const roundAmount = parseFloat(invoice.round_amount || 0);
    
    // Calculate returned amount (price_per_unit already has GST included)
    const totalReturned = invoice.InvoiceItems.reduce((s, i) => {
        const returnedQty = parseFloat(i.returned_quantity || 0);
        if (!returnedQty) return s;
        const pricePerUnit = parseFloat(i.price_per_unit || 0); // Already includes GST
        const discPerUnit = (i.discount_amount && i.quantity) 
            ? (parseFloat(i.discount_amount) / parseFloat(i.quantity)) 
            : 0;
        return s + returnedQty * (pricePerUnit - discPerUnit);
    }, 0);

    const grandTotal = subtotal + roundAmount - totalReturned;

    return (
        <Authenticated>
            <div className="min-h-screen py-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">

                    {/* Action Bar */}
                    <div className="bg-white rounded-2xl shadow-lg p-4 mb-6 border border-teal-100">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <button
                                onClick={() => navigate('/invoice')}
                                className="flex items-center gap-2 px-4 py-2.5 text-teal-700 hover:bg-teal-50 rounded-xl transition-all font-semibold"
                            >
                                <ArrowLeft className="h-5 w-5" />
                                Back to Invoices
                            </button>
                            <button
                                onClick={generatePDF}
                                className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-semibold"
                            >
                                <Download className="h-5 w-5" />
                                Download PDF
                            </button>
                        </div>
                    </div>

                    {/* Invoice Header */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 border border-teal-200">
                        <div className="bg-teal-600 p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                                        <Receipt className="h-8 w-8 text-white" />
                                    </div>
                                    <div>
                                        <h1 className="text-3xl font-bold text-white">Invoice #{invoiceId}</h1>
                                        <p className="text-teal-100 mt-1">Date: {formatDate(invoice.invoice_date)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customer Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white rounded-xl shadow-md p-4 border border-teal-100 hover:shadow-lg transition-shadow">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-teal-100 rounded-lg">
                                    <User className="h-5 w-5 text-teal-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Customer Name</p>
                                    <p className="text-base font-bold text-gray-800">{invoice.name}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-4 border border-blue-100 hover:shadow-lg transition-shadow">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-blue-100 rounded-lg">
                                    <Phone className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Mobile Number</p>
                                    <p className="text-base font-bold text-gray-800">{invoice.mobile_number}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-4 border border-orange-100 hover:shadow-lg transition-shadow">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-orange-100 rounded-lg">
                                    <MapPin className="h-5 w-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Village</p>
                                    <p className="text-base font-bold text-gray-800">{invoice.village}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-4 border border-purple-100 hover:shadow-lg transition-shadow">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-purple-100 rounded-lg">
                                    <Stethoscope className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Doctor</p>
                                    <p className="text-base font-bold text-gray-800">{invoice.doctor || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-4 border border-green-100 hover:shadow-lg transition-shadow">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-green-100 rounded-lg">
                                    <IndianRupee className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Payment Mode</p>
                                    <p className="text-base font-bold text-gray-800">{invoice.payment_mode?.toUpperCase().replace('_', ' ')}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-4 border border-indigo-100 hover:shadow-lg transition-shadow">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-indigo-100 rounded-lg">
                                    <Calendar className="h-5 w-5 text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Invoice Date</p>
                                    <p className="text-base font-bold text-gray-800">{formatDate(invoice.invoice_date)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Medicine Items Table */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6 border border-teal-200">
                        <div className="bg-teal-600 p-4">
                            <h2 className="text-xl font-bold text-white flex items-center gap-3">
                                <Package className="h-6 w-6" />
                                Medicine Items
                            </h2>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b-2 border-teal-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Medicine</th>
                                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">Batch</th>
                                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">Expiry</th>
                                        <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">Qty</th>
                                        <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">Rate</th>
                                        <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">GST</th>
                                        <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">Discount</th>
                                        <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {invoice.InvoiceItems.map((item, i) => (
                                        <tr key={i} className="hover:bg-teal-50/50 transition-colors">
                                            <td className="px-4 py-3 font-semibold text-gray-800">{item.medicines?.name || 'Unknown'}</td>
                                            <td className="px-4 py-3 text-center text-gray-600">{item.batch_number}</td>
                                            <td className="px-4 py-3 text-center text-gray-600">{formatDate(item.expiry_date)}</td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    <span className={`inline-block px-2 py-1 font-bold text-sm rounded-full ${
                                                        item.has_returns 
                                                            ? 'bg-orange-100 text-orange-700 line-through' 
                                                            : 'bg-teal-100 text-teal-700'
                                                    }`}>
                                                        {item.quantity}
                                                    </span>
                                                    {item.has_returns && (
                                                        <>
                                                            <span className="inline-block px-2 py-1 bg-red-100 text-red-700 font-bold text-xs rounded-full">
                                                                -{item.returned_quantity} Returned
                                                            </span>
                                                            <span className="inline-block px-2 py-1 bg-green-100 text-green-700 font-bold text-sm rounded-full">
                                                                = {item.net_quantity} Net
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right font-medium text-gray-700">₹{parseFloat(item.price_per_unit || 0).toFixed(2)}</td>
                                            <td className="px-4 py-3 text-right font-medium text-green-600">+₹{parseFloat(item.gst_amount || 0).toFixed(2)}</td>
                                            <td className="px-4 py-3 text-right font-medium text-red-600">-₹{parseFloat(item.discount_amount || 0).toFixed(2)}</td>
                                            <td className="px-4 py-3 text-right font-bold text-teal-600 text-lg">₹{parseFloat(item.total_price || 0).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Bill Summary */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-teal-200">
                        <div className="bg-teal-600 p-4">
                            <h2 className="text-xl font-bold text-white flex items-center gap-3">
                                <IndianRupee className="h-6 w-6" />
                                Bill Summary
                            </h2>
                        </div>
                        <div className="p-6">
                            <div className="space-y-3">
                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <span className="text-base text-gray-700 font-medium">Sub Total</span>
                                    <span className="text-lg font-bold text-gray-900">₹{subtotal.toFixed(2)}</span>
                                </div>
                                
                                {roundAmount !== 0 && (
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                        <span className="text-base text-gray-700 font-medium">Round Off</span>
                                        <span className={`text-lg font-bold ${roundAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {roundAmount >= 0 ? '+' : ''}₹{roundAmount.toFixed(2)}
                                        </span>
                                    </div>
                                )}

                                {totalReturned > 0 && (
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                        <span className="text-base text-gray-700 font-medium">Returned Amount</span>
                                        <span className="text-lg font-bold text-red-600">-₹{totalReturned.toFixed(2)}</span>
                                    </div>
                                )}
                                
                                <div className="flex justify-between items-center pt-4 -mx-6 px-6 py-4 mt-4 rounded-b-2xl border-t-2 border-teal-600">
                                    <span className="text-xl font-bold text-gray-900">Grand Total</span>
                                    <span className="text-3xl font-bold text-teal-600">₹{grandTotal.toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Payment Details */}
                            {(invoice.payment_mode === 'cash_and_online' || invoice.payment_mode === 'debits') && (
                                <div className="mt-6 p-4 bg-teal-50 rounded-xl border border-teal-200">
                                    <p className="text-sm font-semibold text-gray-700 mb-3">Payment Breakdown:</p>
                                    {invoice.payment_mode === 'cash_and_online' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="text-center p-3 bg-white rounded-lg border border-teal-100">
                                                <p className="text-xs text-gray-600">Cash Amount</p>
                                                <p className="text-xl font-bold text-gray-900">₹{parseFloat(invoice.cash_amount || 0).toFixed(2)}</p>
                                            </div>
                                            <div className="text-center p-3 bg-white rounded-lg border border-teal-100">
                                                <p className="text-xs text-gray-600">Online Amount</p>
                                                <p className="text-xl font-bold text-gray-900">₹{parseFloat(invoice.online_amount || 0).toFixed(2)}</p>
                                            </div>
                                        </div>
                                    )}
                                    {invoice.payment_mode === 'debits' && (
                                        <div className="text-center p-3 bg-white rounded-lg border border-teal-100">
                                            <p className="text-xs text-gray-600">Debits Amount</p>
                                            <p className="text-xl font-bold text-gray-900">₹{parseFloat(invoice.debits_amount || 0).toFixed(2)}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Back Button */}
                    <div className="mt-8 text-center">
                        <button
                            onClick={() => navigate('/invoice')}
                            className="px-10 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all shadow-lg hover:shadow-xl font-semibold"
                        >
                            Back to Invoices
                        </button>
                    </div>
                </div>
            </div>
        </Authenticated>
    );
};

export default ViewInvoice;